/*
 * The replay viewer's arithmetic, kept apart from its rendering so it can be
 * tested without a DOM: which transition is in force at the cursor, which
 * edges it lit, where the next and previous "something happened" instants
 * are, how each input's history becomes a lane, and where playback lands
 * after a frame.
 *
 * Everything here is a pure function of a ReplayResult (chunk 4's output) and
 * a cursor. Time is bigint nanoseconds, as the trace and the samples carry it;
 * only the last step to pixels goes through milliseconds.
 */
import type { TraceEntry } from '../../../sandbox/statechart/protocol';
import { enteredStates, entryCounts, stateChanges, type Transition } from '../../../sandbox/statechart/scan-trace';
import {
  boundsUnknownNote,
  configurationItems,
  enteredItems,
  portNamesText,
  replayCounts,
  stateItems,
  type ReplayContrast,
  type ReplayInput,
  type ReplayResult,
} from '../../../sandbox/statechart/replay';
import type { SimulationResult } from '../../../sandbox/statechart/simulate';
import { edgeKey } from '../../shell/vitesse-statechart';

export const NS_PER_MS = 1_000_000n;
export const toMs = (ns: bigint): number => Number(ns / NS_PER_MS);

/** Index of the last trace entry at or before `tsNs`, or -1. The trace is in
 *  time order, so a binary search keeps a million-entry trace responsive
 *  under a drag. */
function lastIndexAtOrBefore(trace: readonly TraceEntry[], tsNs: bigint): number {
  let lo = 0;
  let hi = trace.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (trace[mid].tsNs <= tsNs) lo = mid + 1;
    else hi = mid - 1;
  }
  return lo - 1;
}

/** The transition in force at an instant: the last one at or before it. Its
 *  `to` is the active configuration; it is also the edge that "just fired". */
export function transitionAt(trace: readonly TraceEntry[], tsNs: bigint): Transition | null {
  for (let i = lastIndexAtOrBefore(trace, tsNs); i >= 0; i--) {
    const e = trace[i];
    if (e.kind === 'transition') return e;
  }
  return null;
}

/** The edge keys a transition lit, for `<vitesse-statechart .firing>`. A
 *  timer-fired transition carries `timer` as its event — the delayed event's
 *  own name never reaches the trace — so it matches by endpoints alone. The
 *  boot transition has no source and lights nothing. */
export function firingKeys(t: Transition | null): string[] {
  if (!t || !t.from) return [];
  const ev = t.event === 'timer' ? '*' : t.event;
  return t.to.map((to) => edgeKey(t.from, to, ev));
}

/** Every instant a person can step to: the window's ends, every recorded
 *  change, and every trace entry. Sorted and unique. */
export function instants(r: Pick<ReplayResult, 'window' | 'samples' | 'trace'>): bigint[] {
  const set = new Set<bigint>([r.window.t0Ns, r.window.t1Ns]);
  for (const s of r.samples) set.add(s.tsNs);
  for (const e of r.trace) set.add(e.tsNs);
  return [...set].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
}

/** The first instant strictly after the cursor, or null at the end. */
export function stepAfter(list: readonly bigint[], cursorNs: bigint): bigint | null {
  let lo = 0;
  let hi = list.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (list[mid] <= cursorNs) lo = mid + 1;
    else hi = mid;
  }
  return lo < list.length ? list[lo] : null;
}

/** The last instant strictly before the cursor, or null at the start. */
export function stepBefore(list: readonly bigint[], cursorNs: bigint): bigint | null {
  let lo = 0;
  let hi = list.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (list[mid] < cursorNs) lo = mid + 1;
    else hi = mid;
  }
  return lo > 0 ? list[lo - 1] : null;
}

// ── lanes ───────────────────────────────────────────────────────────────────

/** How a lane draws: numbers as a line, booleans and text as labelled steps.
 *  Typed, so a bool never becomes a 0/1 line and a string never vanishes. */
export type LaneKind = 'number' | 'boolean' | 'text';

export interface LanePoint {
  tsNs: bigint;
  value: unknown;
}

export interface LaneSeries {
  input: ReplayInput;
  /** The name of the input that reads it and the symbol, or just the symbol when no name is known. */
  label: string;
  kind: LaneKind;
  /** The seed at t0 (if any) followed by every recorded change in the window —
   *  and, on a simulated lane, the sidecar's values after the fork. */
  points: LanePoint[];
  /** Where a simulation took this symbol over, or absent on a replay lane. */
  fork?: LaneFork | null;
}

/**
 * A SIMULATED LANE'S SECOND HALF. Up to `forkNs` the points are the recording;
 * after it they are the sidecar's. Two more series ride along: the recording
 * the simulation stopped following (the ghost, drawn faintly behind) and what
 * the sidecar predicted while the recording was still the truth (the shadow,
 * a thin line over it — the shadow check, made visible).
 */
export interface LaneFork {
  forkNs: bigint;
  /** False for a neighbour: drawn, never handed to the model — and tagged so. */
  readByModel: boolean;
  ghost: LanePoint[];
  shadow: LanePoint[];
}

/**
 * The recording up to and including the fork, and the sidecar from the fork on.
 * The fork instant belongs to BOTH, so a line is continuous there and a step
 * ends where the next begins — the simulation starts from the recorded value.
 */
export function splitAtFork(points: readonly LanePoint[], forkNs: bigint): { before: LanePoint[]; after: LanePoint[] } {
  return {
    before: points.filter((p) => p.tsNs <= forkNs),
    after: points.filter((p) => p.tsNs >= forkNs),
  };
}

/** Everything a lane draws, for its vertical scale: its points, its ghost and its shadow. */
export function laneExtent(lane: Pick<LaneSeries, 'points' | 'fork'>): LanePoint[] {
  return lane.fork ? [...lane.points, ...lane.fork.ghost, ...lane.fork.shadow] : lane.points;
}

/**
 * One lane per input, then one per neighbour the sidecar took over, each with
 * the simulation's own merged points (recorded, then simulated). A neighbour
 * the sidecar left alone gets no lane: it was shown to the sidecar's author,
 * not to the person, and nothing happened to it.
 */
export function simulationLanes(
  r: Pick<SimulationResult, 'inputs' | 'neighbours' | 'samples' | 'recordedSamples' | 'simulated' | 'predictions' | 'window'>,
): LaneSeries[] {
  const forkOf = new Map(r.simulated.map((s) => [s.path, s]));
  const lanes: LaneSeries[] = [];
  for (const input of [...r.inputs, ...r.neighbours.filter((n) => forkOf.has(n.path))]) {
    const points: LanePoint[] = [];
    if (input.seed) points.push({ tsNs: r.window.t0Ns, value: input.seed.value });
    for (const s of r.samples) if (s.path === input.path) points.push({ tsNs: s.tsNs, value: s.value });
    const f = forkOf.get(input.path);
    const fork: LaneFork | null = f
      ? {
          forkNs: f.forkNs,
          readByModel: f.readByModel,
          ghost: r.recordedSamples.filter((s) => s.path === input.path && s.tsNs > f.forkNs).map((s) => ({ tsNs: s.tsNs, value: s.value })),
          shadow: (r.predictions[input.path] ?? []).filter((p) => p.tsNs <= f.forkNs).map((p) => ({ tsNs: p.tsNs, value: p.value })),
        }
      : null;
    lanes.push({ input, label: laneLabel(input), kind: laneKind(laneExtent({ points, fork })), points, fork });
  }
  return lanes;
}

/** The lane's name. The DECODED symbol, as every other surface shows it — a
 *  lane headed `DB10_Motor1_Speed` is the wire form leaking into a person's view. */
export function laneLabel(input: Pick<ReplayInput, 'name' | 'symbolDecoded'>): string {
  return input.name ? `${input.name} · ${input.symbolDecoded}` : input.symbolDecoded;
}

export function laneKind(points: readonly LanePoint[]): LaneKind {
  if (points.length === 0) return 'text';
  if (points.every((p) => typeof p.value === 'number')) return 'number';
  if (points.every((p) => typeof p.value === 'boolean')) return 'boolean';
  return 'text';
}

/** One lane per input, in input order, each with its own typed point list. */
export function laneSeries(r: Pick<ReplayResult, 'inputs' | 'samples' | 'window'>): LaneSeries[] {
  return r.inputs.map((input) => {
    const points: LanePoint[] = [];
    if (input.seed) points.push({ tsNs: r.window.t0Ns, value: input.seed.value });
    for (const s of r.samples) if (s.path === input.path) points.push({ tsNs: s.tsNs, value: s.value });
    return { input, label: laneLabel(input), kind: laneKind(points), points };
  });
}

/**
 * A written path as one strip: what the model would have set, and what that
 * path actually recorded over the same window.
 *
 * Both halves on one lane on purpose. Split across two lanes they read as two
 * unrelated facts; together they read as the question a person actually has —
 * "it says it opened the drain, so why is the level still climbing?" — with its
 * answer beside it.
 */
export interface ContrastLane {
  contrast: ReplayContrast;
  label: string;
  /** The path's own recorded history as lane points; empty when nothing was
   *  recorded, and `null` when it was never asked (see ReplayContrast). */
  points: LanePoint[] | null;
  kind: LaneKind;
  /** The instants the model would have written, and to what. */
  wrote: { tsNs: bigint; value: unknown }[];
}

export function contrastLanes(r: Pick<ReplayResult, 'contrast'>): ContrastLane[] {
  return r.contrast.map((c) => {
    const points = c.recorded === null ? null : c.recorded.map((s) => ({ tsNs: s.tsNs, value: s.value }));
    return { contrast: c, label: c.symbolDecoded, points, kind: laneKind(points ?? []), wrote: c.wrote };
  });
}

// ── which lanes are on screen ───────────────────────────────────────────────

/*
 * WHICH LANES ARE ON SCREEN IS A VIEW, NOT A FACT ABOUT THE REPLAY.
 *
 * A model with a dozen inputs draws a dozen lanes and a chart nobody can take in
 * at once, so a person can put some away. Nothing here touches the result: the
 * trace, the summary and every count below the chart go on describing the whole
 * replay, and the viewer says in words when the picture is narrower than the
 * account (see the hidden-count line).
 *
 * HIDING IS KEYED BY PATH, NOT BY NAME — two inputs can be connected to one
 * symbol, and an input of a child is named with its machine — and RECORDED INPUTS ARE
 * KEYED APART FROM WRITTEN PATHS. A model that reads a symbol and also writes it
 * has two lanes for it, on either side of the "would have done" line; one key
 * would put both away together, which is the one pairing a person most wants to
 * keep on screen.
 */
export function inputLaneKey(lane: Pick<LaneSeries, 'input'>): string {
  return `in:${lane.input.path}`;
}

export function contrastLaneKey(lane: Pick<ContrastLane, 'contrast'>): string {
  return `out:${lane.contrast.path}`;
}

/** The lanes still on screen, in their own order. */
export function visibleLanes<T>(lanes: readonly T[], hidden: ReadonlySet<string>, key: (lane: T) => string): T[] {
  return lanes.filter((lane) => !hidden.has(key(lane)));
}

/**
 * The choice carried into a NEW result: every hidden key the new result still
 * has, and nothing else.
 *
 * Re-running the same model over a different window must not quietly bring back
 * lanes a person put away — the window moved, not their mind. But a key that the
 * new result has no lane for is a leftover from another document, and keeping it
 * would hide a symbol of the same name later for no reason anyone could see.
 */
export function keepHidden(hidden: ReadonlySet<string>, keys: readonly string[]): Set<string> {
  const live = new Set(keys);
  return new Set([...hidden].filter((k) => live.has(k)));
}

/** The point in force at an instant — the last at or before it — or null if
 *  the input had no value yet (unknown, not zero). */
export function valueAt(points: readonly LanePoint[], tsNs: bigint): LanePoint | null {
  let lo = 0;
  let hi = points.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (points[mid].tsNs <= tsNs) lo = mid + 1;
    else hi = mid;
  }
  return lo > 0 ? points[lo - 1] : null;
}

/** A numeric lane's vertical range with a little air; a constant reads as a
 *  band of ±1 rather than a line on the floor. */
export function numberRange(points: readonly LanePoint[]): { min: number; max: number } | null {
  let lo = Infinity;
  let hi = -Infinity;
  for (const p of points) {
    if (typeof p.value !== 'number' || !Number.isFinite(p.value)) continue;
    if (p.value < lo) lo = p.value;
    if (p.value > hi) hi = p.value;
  }
  if (lo > hi) return null;
  if (lo === hi) return { min: lo - 1, max: hi + 1 };
  const pad = (hi - lo) * 0.08;
  return { min: lo - pad, max: hi + pad };
}

/** A sample's value as a person reads it. `—` for "no value yet". */
export function formatSampleValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'number') {
    if (Number.isInteger(v)) return String(v);
    const s = Math.abs(v) >= 1000 ? v.toFixed(1) : v.toFixed(3);
    return s.replace(/\.?0+$/, '');
  }
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'string') return v;
  return JSON.stringify(v);
}

// ── the plain story ─────────────────────────────────────────────────────────

/**
 * Which moves entered a chosen state — for each move in `moves` (the output of
 * `stateChanges`), the chosen states it newly entered, in the order they
 * appear in its configuration; empty when it entered none. Uses the search's
 * own rule (`enteredStates`), so the timeline lights exactly what the search
 * matched. The first move is the boot: where the model starts, not something
 * it did, so it is never highlighted — as the search never counts it.
 */
export function highlightedMoves(moves: readonly Transition[], chosen: readonly string[]): string[][] {
  if (chosen.length === 0) return moves.map(() => []);
  const want = new Set(chosen);
  return moves.map((m, i) => (i === 0 ? [] : enteredStates(moves[i - 1].to, m.to).filter((s) => want.has(s))));
}

/**
 * Which label each write on a would-write lane gets: the whole "would set true",
 * just the value "true" when only that fits, or none. A label sits right of its
 * tick and must end before the next tick, and must start clear of the label
 * before it, so two writes a few seconds apart never print over each other.
 * Every tick keeps its line and its hover title whatever its label.
 *
 * The value is the half worth keeping: the lane's own heading already says
 * "would write", so a crowded stretch reading "true · false · true" loses
 * nothing a person needs.
 */
export function writeLabels(xs: readonly number[], full: readonly number[], short: readonly number[]): ('full' | 'short' | null)[] {
  const GAP = 4;
  let lastEnd = -Infinity;
  return xs.map((x, i) => {
    const room = (i + 1 < xs.length ? xs[i + 1] : Infinity) - x;
    if (x < lastEnd + GAP) return null;
    const pick = room > full[i] ? 'full' : room > short[i] ? 'short' : null;
    if (pick) lastEnd = x + (pick === 'full' ? full[i] : short[i]);
    return pick;
  });
}

/**
 * Which ticks on a lane get a text label. A label sits just right of its tick
 * and needs `widths[i]` px before the next tick, and a label starts at least
 * 44 px after the previous one — so no tick strikes through a label and labels
 * never crowd. Every tick keeps its hover title either way.
 *
 * MARKED TICKS CHOOSE FIRST, AND MAY CROSS THE OTHERS. Where ticks crowd,
 * first-come labelling named whichever came first — typically the move back to
 * Normal — and left the move a person searched for unnamed. Worse, the move
 * into a state is usually followed seconds later by the move out, so a marked
 * label rarely has room before the NEXT tick. So the marked ticks claim their
 * labels in a first pass, needing room only before the next MARKED tick; an
 * unmarked tick under a marked label keeps its line and loses its label (the
 * view draws marked labels over a backing stroke so they stay legible). The
 * rest fill in around them in a second pass, by the ordinary rule.
 */
export function tickLabels(xs: readonly number[], widths: readonly number[], marked: readonly boolean[]): boolean[] {
  const n = xs.length;
  /** For each i, the x of the next j > i where `on(j)` — one backward pass, so
   *  the whole labelling stays linear in the tick count. */
  const nextWhere = (on: (j: number) => boolean): number[] => {
    const next = new Array<number>(n).fill(Infinity);
    for (let i = n - 2; i >= 0; i--) next[i] = on(i + 1) ? xs[i + 1] : next[i + 1];
    return next;
  };
  const nextMarked = nextWhere((j) => marked[j]);
  const out = new Array<boolean>(n).fill(false);
  let last = -Infinity;
  for (let i = 0; i < n; i++) {
    if (marked[i] && xs[i] - last > 44 && nextMarked[i] - xs[i] > widths[i]) {
      out[i] = true;
      last = xs[i];
    }
  }
  // Second pass: unmarked ticks, clear of every label already placed — past
  // the end of the one before, and 44 px short of the next one's start. Only
  // marked labels lie ahead of any tick this pass visits, so their positions
  // are fixed before it starts.
  const nextLabelled = nextWhere((j) => out[j]);
  last = -Infinity;
  let lastEnd = -Infinity;
  for (let i = 0; i < n; i++) {
    if (out[i]) {
      last = xs[i];
      lastEnd = xs[i] + widths[i];
      continue;
    }
    if (marked[i]) continue;
    const clear = xs[i] - last > 44 && xs[i] > lastEnd && nextLabelled[i] - xs[i] > 44;
    const nextTick = i + 1 < n ? xs[i + 1] : Infinity;
    if (clear && nextTick - xs[i] > widths[i]) {
      out[i] = true;
      last = xs[i];
      lastEnd = xs[i] + widths[i];
    }
  }
  return out;
}

export type Write = Extract<TraceEntry, { kind: 'write' }>;

/**
 * What a replay found, in the terms a person asks about — where it started,
 * whether it moved, what it would have done to the machine, and how much it
 * had to go on. The viewer turns this into sentences; the AI keeps the
 * engineer's paragraph (`ReplayResult.summary`).
 */
export interface ReplayStory {
  /** The configuration it booted into, or '' if it never started. */
  start: string;
  end: string;
  /** The same configurations as a person reads them: the states of an array's machines folded. */
  startStates: string[];
  endStates: string[];
  /** Real state changes after boot — the first transition is always the boot. */
  moves: Transition[];
  /** Each state entered after boot, with how often, in order of first entry. An array's machines are folded. */
  entered: { state: string; count: number }[];
  /** Declared states it never entered, boot included. An array's machines are folded. */
  neverReached: string[];
  writes: Write[];
  readings: number;
  /** A person's name for each input: the input's name, else the symbol. The ports of an array's machines are folded. */
  inputNames: string[];
  /** Inputs with no value when the window opened, and when one first arrived (null: never). */
  gaps: { name: string; untilNs: bigint | null }[];
  truncated: string[];
  errors: number;
  hung: boolean;
  /** What the backend said that still matters. "Could not check whether X has
   *  history" is dropped once X's readings came back — they answer it. */
  notes: string[];
}

export function replayStory(r: ReplayResult): ReplayStory {
  const nameOf = (i: ReplayInput) => i.name ?? i.symbolDecoded;
  // The first change is the boot. Its `from` is NOT empty — the engine
  // reports the initial transition with a source id (the root's, today) — so
  // it is recognised by position, and entries are counted from where it landed.
  const { moves, writes, errors } = replayCounts(r.trace);
  const entered = entryCounts(moves, stateChanges(r.trace)[0]?.to ?? []);
  const byPath = new Map(r.inputs.map((i) => [i.path, i]));
  return {
    start: r.initialState.join('+'),
    end: r.finalState.join('+'),
    startStates: configurationItems(r.initialState, r.stateIds),
    endStates: configurationItems(r.finalState, r.stateIds),
    moves,
    entered: enteredItems(entered, r.stateIds),
    neverReached: stateItems(r.neverEntered, r.stateIds),
    writes,
    readings: r.rowCount,
    inputNames: portNamesText(r.inputs.map(nameOf), r.stateIds),
    gaps: r.inputs.filter((i) => i.seed === null).map((i) => ({ name: nameOf(i), untilNs: i.unknownUntilNs })),
    truncated: r.truncated.map((p) => {
      const input = byPath.get(p);
      return input ? nameOf(input) : p;
    }),
    errors: errors.length,
    hung: r.hung,
    notes: r.notes.filter((n) => !r.inputs.some((i) => i.rows > 0 && n === boundsUnknownNote(i.controllerId))),
  };
}

/*
 * A LONG LIST, CUT TO WHAT A PERSON READS FIRST.
 *
 * A group with 16 machines entered, never reached and read dozens of things,
 * and a summary that names each one buries the answer. So a list shows its
 * first few and counts the rest, and the count opens it. It is cut only when
 * at least 2 would be left out: "and 1 more" takes the room of the one.
 */
export function cutList<T>(items: readonly T[], shown: number, open: boolean): { shown: readonly T[]; hidden: number; cuttable: boolean } {
  const cuttable = items.length > shown + 1;
  if (!cuttable || open) return { shown: items, hidden: 0, cuttable };
  return { shown: items.slice(0, shown), hidden: items.length - shown, cuttable };
}

/** Lines in runs: each line with a kind joins the line before it when that has the same kind. */
export function runsOf<T extends { kind?: string }>(lines: readonly T[]): Array<{ kind: string | undefined; lines: T[] }> {
  const runs: Array<{ kind: string | undefined; lines: T[] }> = [];
  for (const l of lines) {
    const last = runs[runs.length - 1];
    if (l.kind !== undefined && last?.kind === l.kind) last.lines.push(l);
    else runs.push({ kind: l.kind, lines: [l] });
  }
  return runs;
}

// ── wall-clock times ────────────────────────────────────────────────────────

/*
 * ONE WAY TO WRITE A MOMENT on the Simulate panel and in the replay under it:
 * the activity list, the quick-replay option, the story, the transport clock
 * and every tick's hover title. Local time, 24-hour, in the UI's locale — so a
 * German reader gets "Di., 15. Sept." and nobody gets a US month/day.
 *
 * The date is shown once the span is a day or longer; below that the time is
 * unambiguous, and above it "from 09:05 to 09:05" would not be.
 */
const DAY_MS = 86_400_000;

export function spanShowsDate(spanMs: number): boolean {
  return spanMs >= DAY_MS;
}

const dtfCache = new Map<string, Intl.DateTimeFormat>();
function dtf(locale: string, date: boolean, seconds: boolean): Intl.DateTimeFormat {
  const key = `${locale}|${date}|${seconds}`;
  let f = dtfCache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(locale, {
      ...(date ? { weekday: 'short', day: 'numeric', month: 'short' } : {}),
      hour: '2-digit',
      minute: '2-digit',
      ...(seconds ? { second: '2-digit' } : {}),
      hourCycle: 'h23',
    });
    dtfCache.set(key, f);
  }
  return f;
}

/** A moment, local time: "14:02", or "Tue 15 Sep, 14:02" with the date. */
export function formatMoment(ms: number, locale: string, opts: { date: boolean; seconds?: boolean }): string {
  return dtf(locale, opts.date, !!opts.seconds).format(new Date(ms));
}

/** A span, with the date once when it stays within a day: "Tue 15 Sep, 14:02–14:09". */
export function formatSpan(t0Ms: number, t1Ms: number, locale: string): string {
  const sameDay = new Date(t0Ms).toDateString() === new Date(t1Ms).toDateString();
  return sameDay
    ? `${formatMoment(t0Ms, locale, { date: true })}–${formatMoment(t1Ms, locale, { date: false })}`
    : `${formatMoment(t0Ms, locale, { date: true })} – ${formatMoment(t1Ms, locale, { date: true })}`;
}

/** The mark between the parts of one line — "Fri 23, 16:34", "ps1 clear, ps2 clear" — in the locale's
 *  own punctuation: the ideographic comma in Japanese, a comma and a space elsewhere. */
export function listSep(locale: string): string {
  return new Intl.Locale(locale).language === 'ja' ? '、' : ', ';
}

/** A found period in a table cell: "Mon 28 · 08:29–08:34", the day named once
 *  when it stays within one. Composed rather than asked of Intl as one pattern,
 *  which puts the day before the weekday in plain `en`. */
export function formatWhen(t0Ms: number, t1Ms: number, locale: string): string {
  const weekday = new Intl.DateTimeFormat(locale, { weekday: 'short' });
  const day = (ms: number) => `${weekday.format(new Date(ms))} ${new Date(ms).getDate()}`;
  const time = (ms: number) => formatMoment(ms, locale, { date: false });
  return new Date(t0Ms).toDateString() === new Date(t1Ms).toDateString()
    ? `${day(t0Ms)} · ${time(t0Ms)}–${time(t1Ms)}`
    : `${day(t0Ms)} · ${time(t0Ms)} – ${day(t1Ms)} · ${time(t1Ms)}`;
}

/** How long a found period lasted, in the locale's short units: "45 sec", "5 min", "1 hr 20 min". */
export function formatLength(ms: number, locale: string): string {
  const unit = (n: number, u: 'second' | 'minute' | 'hour') =>
    new Intl.NumberFormat(locale, { style: 'unit', unit: u, unitDisplay: 'short', maximumFractionDigits: 0 }).format(n);
  const sec = Math.round(ms / 1000);
  if (sec < 60) return unit(sec, 'second');
  const min = Math.round(sec / 60);
  if (min < 60) return unit(min, 'minute');
  const h = Math.floor(min / 60);
  const rest = min % 60;
  return rest ? `${unit(h, 'hour')} ${unit(rest, 'minute')}` : unit(h, 'hour');
}

// ── playback ────────────────────────────────────────────────────────────────

/** Recorded seconds per real second; `max` jumps to the next instant on every
 *  frame, so a day of quiet history goes by in as many frames as it has
 *  changes. */
export type Speed = 1 | 10 | 60 | 'max';
export const SPEEDS: readonly Speed[] = [1, 10, 60, 'max'];

/** Where the cursor lands after `wallMs` of real time. Never past `t1Ns`. */
export function advance(cursorNs: bigint, wallMs: number, speed: Speed, list: readonly bigint[], t1Ns: bigint): bigint {
  if (speed === 'max') return stepAfter(list, cursorNs) ?? t1Ns;
  const next = cursorNs + BigInt(Math.max(0, Math.round(wallMs * speed))) * NS_PER_MS;
  return next > t1Ns ? t1Ns : next;
}

// ── the view ────────────────────────────────────────────────────────────────

/**
 * At most a handful of points per pixel column, chosen so the picture does not change: the first
 * and last point in each column, and for a number also its lowest and highest. A line through them
 * draws the same shape as a line through every point — a one-sample spike is its column's highest,
 * so it is never thinned away — and a step lane still changes value where the recording did, to the
 * pixel. Order and the points themselves are kept, so a caller can still tell the lane's last point
 * by identity.
 *
 * Why: at whole-window zoom a day of one symbol is ~340,000 points in a lane a thousand pixels wide,
 * and every one of them became a path vertex and a circle on every render.
 */
export function thinToPixels<T extends { value: unknown }>(points: readonly T[], xOf: (p: T) => number, numeric: boolean): T[] {
  const out: T[] = [];
  let column = NaN;
  let first = -1;
  let last = -1;
  let low = -1;
  let high = -1;
  const flush = () => {
    if (first < 0) return;
    const keep = numeric ? [first, low, high, last] : [first, last];
    for (const i of [...new Set(keep)].filter((i) => i >= 0).sort((a, b) => a - b)) out.push(points[i]);
  };
  for (let i = 0; i < points.length; i++) {
    const c = Math.floor(xOf(points[i]));
    if (c !== column) {
      flush();
      column = c;
      first = i;
      low = -1;
      high = -1;
    }
    last = i;
    const v = points[i].value;
    if (numeric && typeof v === 'number') {
      if (low < 0 || v < (points[low].value as number)) low = i;
      if (high < 0 || v > (points[high].value as number)) high = i;
    }
  }
  flush();
  return out;
}

/**
 * The part of a time-sorted list a view can show: everything inside
 * [startMs, endMs], plus one neighbour on each side. The neighbour before is
 * the value that HOLDS at the view's left edge (a step rect, a line's start);
 * the neighbour after is where the last visible step ends and the line goes.
 * Both land off the plot and are clipped; without them the lanes would start
 * and stop short of the edges. Binary search: a million-sample lane at any
 * zoom costs two probes and a slice, not a scan.
 */
export function visibleSlice<T extends { tsNs: bigint }>(items: readonly T[], startMs: number, endMs: number): T[] {
  if (items.length === 0) return [];
  // First index with ts >= startMs.
  let lo = 0;
  let hi = items.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (toMs(items[mid].tsNs) < startMs) lo = mid + 1;
    else hi = mid;
  }
  const from = Math.max(0, lo - 1);
  // First index with ts > endMs.
  lo = from;
  hi = items.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (toMs(items[mid].tsNs) <= endMs) lo = mid + 1;
    else hi = mid;
  }
  const to = Math.min(items.length, lo + 1);
  return items.slice(from, to);
}

/** Where the playhead is put back on screen when it leaves the view: at
 *  this fraction of the plot from the left, so most of the plot is ahead. */
export const FOLLOW_FRAC = 0.25;

/**
 * Keep a moving cursor in view. Inside the view, nothing changes — the same
 * object comes back, so a caller can compare by identity. Outside it, the view
 * slides so the cursor sits FOLLOW_FRAC across the plot, then is held inside
 * `bounds` (the whole window), which at the very end means the cursor drifts
 * past the fraction rather than the view showing what is beyond the window.
 */
export function followCursor(
  view: { startMs: number; endMs: number },
  cursorMs: number,
  bounds: { startMs: number; endMs: number },
): { startMs: number; endMs: number } {
  if (cursorMs >= view.startMs && cursorMs <= view.endMs) return view;
  const span = view.endMs - view.startMs;
  let startMs = cursorMs - span * FOLLOW_FRAC;
  if (startMs + span > bounds.endMs) startMs = bounds.endMs - span;
  if (startMs < bounds.startMs) startMs = bounds.startMs;
  return { startMs, endMs: startMs + span };
}

/**
 * A press on the overview strip at `tMs`. Inside the view, the view stays
 * where it is (the same object) and the press remembers how far into it the
 * pointer is, so a drag carries the segment without a jump. Outside, the view
 * is centred under the pointer and the drag continues from its middle.
 */
export function overviewView(
  view: { startMs: number; endMs: number },
  tMs: number,
): { view: { startMs: number; endMs: number }; offsetMs: number } {
  const span = view.endMs - view.startMs;
  if (tMs >= view.startMs && tMs <= view.endMs) return { view, offsetMs: tMs - view.startMs };
  const offsetMs = span / 2;
  return { view: { startMs: tMs - offsetMs, endMs: tMs - offsetMs + span }, offsetMs };
}
